import gql from 'graphql-tag'
import Model, { encoders, ID, IsoDateTime, JsonObject, MODEL_LEVEL } from 'src/models/model'
import RegisterClient from 'src/models/register/RegisterClient'
import Site from 'src/models/site/Site'

export default class Hardware extends Model {
  static name = 'Hardware'

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.HardwareAndRegisterStation]
  static manageAction = ['Manage', 'ManagePrinterRouting']

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        default: null
      },
      machineUid: {
        type: String,
        output: true
      },
      config: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            role: 'register',

            wifiEnabled: false,
            wifiSsid: null,
            wifiPassword: null,

            disableOverscan: true,
            displayRotateIndex: 0,

            displayUrl: 'https://google.com',

            hasReceiptPrinter: true,
            hasCashDrawer: true,

            hasRfidReader: true,
            hasMagneticCardReader: true,
            hasQrCodeReader: true,

            receiptPaperWidth: 576 // Default width for Epson 80mm receipt paper
          }
        }
      },
      status: {
        type: JsonObject,
        default () {
          return {
          }
        },
        output: true
      },
      lastSeenAt: {
        type: IsoDateTime,
        output: true
      },
      registerClientId: {
        type: ID,
        default: null
      },
      registerClient: {
        type: RegisterClient,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      site: {
        type: Site,
        encode: encoders.hasOne,
        reference: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true
      },
      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      errorCount () {
        if (!this.config) return null
        if (!this.status) return null

        return _.compact([
          (!this.status.connected) || null,
          (this.config.hasRfidReader && !this.status.rfidReaderConnected) || null,
          (this.config.hasReceiptPrinter && !this.status.receiptPrinterConnected) || null,
          (this.config.hasMagneticCardReader && !this.status.magneticCardReaderConnected) || null
        ]).length
      }
    }

    this.queries = {
      unassignedHardware: {
        results: {
          hardwares: [Hardware]
        }
      }
    }
  }

  static roleOptions = [
    { value: 'register', label: 'Register' },
    { value: 'display', label: 'Display' },
    { value: 'printer', label: 'Printer' },
    { value: 'ticket-kiosk', label: 'Ticket Kiosk' }
  ]

  static displayRotationIndexOptions = [
    { value: 0, label: 'No Rotation' },
    { value: 1, label: '90 degrees' },
    { value: 2, label: '180 degrees' },
    { value: 3, label: '270 degrees' }
  ]

  async sendRemoteCommand (command, data = {}) {
    const mutation = gql`mutation ($input: SendRemoteCommandInput!){
      sendRemoteCommand(input: $input) {
        recipientCount
        errors {
          code
          description
        }
      }
    }`

    const destination = `hardware-${this.id}`

    const result = await Model.apollo.mutate({
      mutation,
      variables: {
        input: {
          remoteCommandInput: {
            command,
            data,
            destination
          }
        }
      }
    })

    return result.data.sendRemoteCommand
  }

  async triggerAction (action) {
    this.action = action

    let result
    try {
      result = await this.save()
      if (result.errors.length) {
        console.log(result.errors)
        this.$mh.showErrors(result.errors)
      }
    } catch (error) {
      console.log(error)
      return false
    } finally {
      this.resetField('action')
    }

    return result.errors.length === 0
  }
}
